import { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { apiService } from '../services/api';
import { OtpCodeInput } from './OtpCodeInput';
import { isCompleteOtp, mapLoginError, resolveLoginOutcome, type LoginStep } from '../utils/twoFactor';

interface LoginProps {
  onLogin: (user: { id: string; email: string; name?: string; role?: string; isAuthenticated: boolean }) => void;
}

export function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // Verhindert Doppel-Submits (Auto-Submit bei vollständigem Code + Enter), die das Rate-Limit belasten.
  const inFlight = useRef(false);

  /** Sendet die Zugangsdaten (und im 2FA-Schritt den Code) an das Backend und wertet die Antwort aus. */
  const submitLogin = async (code?: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setIsLoading(true);

    try {
      const response = await apiService.login(code ? { email, password, twoFactorCode: code } : { email, password });
      const outcome = resolveLoginOutcome(response);

      if (outcome === 'two-factor-required') {
        setTwoFactorCode('');
        setStep('two-factor');
        return;
      }
      if (outcome === 'invalid') {
        setError(mapLoginError(null, step));
        return;
      }

      // Access-Token nur in-memory; der Refresh-Token kommt als httpOnly-Cookie (F-9).
      apiService.setToken(response.access_token);

      onLogin({
        id: response.admin.id,
        email: response.admin.email,
        name: response.admin.name,
        role: response.admin.role,
        isAuthenticated: true
      });
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(mapLoginError(err, step));
      if (step === 'two-factor') setTwoFactorCode('');
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'two-factor') {
      if (isCompleteOtp(twoFactorCode)) await submitLogin(twoFactorCode);
      return;
    }
    await submitLogin();
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setTwoFactorCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">AI Platform Admin</CardTitle>
          <CardDescription className="text-center">
            {step === 'two-factor'
              ? 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein'
              : 'Melden Sie sich mit Ihren Zugangsdaten an'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert className="mb-4 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 'two-factor' ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="two-factor-code" className="justify-center">Bestätigungscode</Label>
                  <OtpCodeInput
                    id="two-factor-code"
                    value={twoFactorCode}
                    onChange={setTwoFactorCode}
                    onComplete={(code) => void submitLogin(code)}
                    disabled={isLoading}
                    invalid={!!error}
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading || !isCompleteOtp(twoFactorCode)}>
                  {isLoading ? 'Wird verarbeitet...' : 'Bestätigen'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToCredentials}
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Wird verarbeitet...' : 'Anmelden'}
                </Button>
              </>
            )}
          </form>

        </CardContent>
      </Card>
    </div>
  );
}